import React, { useState, useEffect } from 'react';
import { useApp } from '../context/AppContext';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Calculator, Users, Package, Settings2, Save, FileDown, TrendingUp } from 'lucide-react';
import useIsMobile from '../hooks/useIsMobile';

const fmt = (n) => new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2 }).format(n || 0);

function InputField({ label, value, onChange, type = 'number', min, max, step, note, options }) {
  const [showInfo, setShowInfo] = useState(false);
  const lowerLabel = String(label || '').toLowerCase();
  const numericValue = Number(value);
  const hasNumericValue = Number.isFinite(numericValue);
  const isKgField = lowerLabel.includes('(kg)');
  const isKmField = lowerLabel.includes('(km)');
  const lbsValue = hasNumericValue ? (numericValue * 2.20462) : 0;
  const milesValue = hasNumericValue ? (numericValue * 0.621371) : 0;

  return (
    <div>
      <label className="label" style={{ display:'flex', alignItems:'center', gap:'0.4rem' }}>
        <span>{label}</span>
        {note && (
          <button
            type="button"
            onClick={() => setShowInfo((s) => !s)}
            style={{ width:18, height:18, borderRadius:'50%', border:'1px solid var(--surface-high)', background:'white', color:'var(--primary)', fontSize:'0.72rem', fontWeight:700, cursor:'pointer', lineHeight:1 }}
            aria-label="Info"
            title="Info"
          >
            i
          </button>
        )}
      </label>
      {options ? (
        <select className="input" value={value} onChange={e => onChange(e.target.value)}>
          {options.map(o => <option key={o.val} value={o.val}>{o.label}</option>)}
        </select>
      ) : (
        <input className="input" type={type} value={value}
          onChange={e => onChange(type === 'number' ? parseFloat(e.target.value) || 0 : e.target.value)}
          min={min} max={max} step={step} />
      )}
      {!options && type === 'number' && hasNumericValue && isKgField && (
        <div style={{ fontSize: '0.75rem', color: 'var(--on-surface-muted)', marginTop: '0.25rem' }}>
          {lbsValue.toFixed(1)} lbs
        </div>
      )}
      {!options && type === 'number' && hasNumericValue && isKmField && (
        <div style={{ fontSize: '0.75rem', color: 'var(--on-surface-muted)', marginTop: '0.25rem' }}>
          {milesValue.toFixed(2)} mi
        </div>
      )}
      {note && showInfo && (
        <div style={{ fontSize: '0.75rem', color: 'var(--on-surface-muted)', marginTop: '0.25rem', background:'var(--surface-low)', border:'1px solid var(--surface-high)', borderRadius:8, padding:'0.5rem 0.625rem' }}>
          {note}
        </div>
      )}
    </div>
  );
}

function ToggleField({ label, value, onChange, note }) {
  const { t } = useTranslation();
  const [showInfo, setShowInfo] = useState(false);
  return (
    <div>
      <label className="label" style={{ display:'flex', alignItems:'center', gap:'0.4rem' }}>
        <span>{label}</span>
        {note && (
          <button
            type="button"
            onClick={() => setShowInfo((s) => !s)}
            style={{ width:18, height:18, borderRadius:'50%', border:'1px solid var(--surface-high)', background:'white', color:'var(--primary)', fontSize:'0.72rem', fontWeight:700, cursor:'pointer', lineHeight:1 }}
            aria-label="Info"
            title="Info"
          >
            i
          </button>
        )}
      </label>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginTop: '0.375rem' }}>
        <label className="toggle">
          <input type="checkbox" checked={value} onChange={e => onChange(e.target.checked)} />
          <span className="toggle-slider" />
        </label>
        <span style={{ fontSize: '0.85rem', color: value ? 'var(--primary)' : 'var(--on-surface-muted)', fontWeight: value ? 600 : 400 }}>
          {value ? t('opex.mm.returnYes') : t('opex.mm.returnNo')}
        </span>
      </div>
      {note && showInfo && (
        <div style={{ fontSize: '0.75rem', color: 'var(--on-surface-muted)', marginTop: '0.375rem', background:'var(--surface-low)', border:'1px solid var(--surface-high)', borderRadius:8, padding:'0.5rem 0.625rem' }}>
          {note}
        </div>
      )}
    </div>
  );
}

function ResultsMM({ result, isMobile }) {
  const { t } = useTranslation();
  if (!result) return (
    <div style={{ textAlign: 'center', padding: '3rem 1rem', color: 'var(--on-surface-muted)' }}>
      <Calculator size={48} style={{ marginBottom: '1rem', opacity: 0.3 }} />
      <p>{t('opex.emptyState')}</p>
    </div>
  );
  const { summary, operations } = result;
  const costPerLb = (summary.costPerKg || 0) / 2.20462;
  const costPerMile = (summary.costPerKm || 0) * 1.60934;
  const items = [
    [t('opex.results.personnel'), summary.personnelCost],
    [t('opex.results.energy'),    summary.energyCost],
    [t('opex.results.maintenance'), summary.maintenanceCost],
  ];
  const outputs = [
    [t('opex.results.mm.totalTrips'),       operations.totalTrips],
    [t('opex.results.mm.tripsPerDrone'),    operations.tripsPerDrone],
    [t('opex.results.mm.opHours'),          operations.totalOperationalHours?.toFixed(2) + ' h'],
    [t('opex.results.mm.flightHours'),      operations.totalFlightHours?.toFixed(2) + ' h'],
    [t('opex.results.mm.energyUsed'),       operations.totalEnergyUsed?.toFixed(2) + ' kWh'],
    [t('opex.results.mm.batteryChanges'),   operations.totalBatteryChanges],
    [t('opex.results.mm.chargeWait'),       operations.waitingForChargeMinutes?.toFixed(1) + ' min'],
    [t('opex.results.co2'),                 operations.co2Emissions?.toFixed(2) + ' kg'],
  ];
  return (
    <div>
      <div className="teal-card" style={{ marginBottom: '1rem' }}>
        <div style={{ fontSize: '0.7rem', opacity: 0.7, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '0.25rem' }}>{t('opex.results.totalCost')}</div>
        <div style={{ fontFamily: 'var(--font-head)', fontSize: '2rem', fontWeight: 800 }}>{fmt(summary.totalCost)}</div>
        <div style={{ display: 'flex', gap: '1rem', opacity: 0.8, fontSize: '0.8rem', marginTop: '0.25rem' }}>
          <span>{fmt(summary.costPerKg)}/kg</span>
          <span>{fmt(summary.costPerKm)}/km</span>
        </div>
        <div style={{ display: 'flex', gap: '1rem', opacity: 0.75, fontSize: '0.74rem', marginTop: '0.15rem' }}>
          <span>{fmt(costPerLb)}/lb</span>
          <span>{fmt(costPerMile)}/mi</span>
        </div>
        <div style={{ marginTop: '1.25rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {items.map(([l, v]) => (
            <div key={l}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', marginBottom: '0.25rem' }}>
                <span style={{ opacity: 0.8 }}>{l}</span>
                <span style={{ fontWeight: 600 }}>{fmt(v)}</span>
              </div>
              <div style={{ height: 4, background: 'rgba(255,255,255,0.2)', borderRadius: 2 }}>
                <div style={{ height: '100%', background: 'rgba(255,255,255,0.8)', borderRadius: 2, width: `${Math.min(100, (v / summary.totalCost) * 100)}%` }} />
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="card">
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem', color: 'var(--primary)' }}>
          <TrendingUp size={18} />
          <span style={{ fontWeight: 700, fontFamily: 'var(--font-head)' }}>{t('opex.results.opOutputs')}</span>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '0.6rem' }}>
          {outputs.map(([label, val]) => (
            <div key={label} style={{ background: 'var(--surface-low)', borderRadius: 'var(--radius)', padding: '0.75rem' }}>
              <div style={{ fontSize: '0.65rem', color: 'var(--on-surface-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</div>
              <div style={{ fontFamily: 'var(--font-head)', fontWeight: 700, fontSize: '1rem', color: 'var(--primary)', marginTop: '0.2rem' }}>{val}</div>
            </div>
          ))}
        </div>
        {operations.batteryOptimizationTip && (
          <div style={{ marginTop: '1rem', background: operations.waitingForChargeMinutes > 0 ? '#fef3c7' : '#d1fae5', borderRadius: 'var(--radius)', padding: '0.875rem', fontSize: '0.8rem' }}>
            {operations.batteryOptimizationTip}
          </div>
        )}
      </div>
    </div>
  );
}

function ResultsSP({ result, isMobile }) {
  const { t } = useTranslation();
  if (!result) return (
    <div style={{ textAlign: 'center', padding: '3rem 1rem', color: 'var(--on-surface-muted)' }}>
      <Calculator size={48} style={{ marginBottom: '1rem', opacity: 0.3 }} />
      <p>{t('opex.emptyState')}</p>
    </div>
  );
  const { summary, operations } = result;
  const items = [
    [t('opex.results.personnel'),   summary.personnelCost],
    [t('opex.results.energy'),      summary.energyCost],
    [t('opex.results.maintenance'), summary.maintenanceCost],
  ];
  const outputs = [
    [t('opex.results.sp.panelsDay'),       operations.panelsPerDay],
    [t('opex.results.sp.cyclePerPanel'),   Math.floor(operations.cycleTimeSec / 60) + 'm ' + (operations.cycleTimeSec % 60).toFixed(0) + 's'],
    [t('opex.results.sp.panelsPerBat'),    operations.panelsPerBattery],
    [t('opex.results.sp.totalDays'),       operations.workDaysNeeded],
    [t('opex.results.sp.totalFlightH'),    operations.totalFlightHours?.toFixed(1) + ' h'],
    [t('opex.results.sp.swapsDay'),        operations.batterySwapsDay],
    [t('opex.results.sp.chargeWaitDay'),   (operations.waitingMinutesDay || 0).toFixed(1) + ' min'],
    [t('opex.results.sp.optimalSets'),     operations.optimalBatterySets],
    [t('opex.results.sp.chargeTimePerBat'),operations.chargingTimePerBattery?.toFixed(1) + ' min'],
    [t('opex.results.sp.totalEnergy'),     operations.totalEnergyUsed?.toFixed(1) + ' kWh'],
    [t('opex.results.co2'),                operations.co2Emissions?.toFixed(2) + ' kg'],
  ];
  return (
    <div>
      <div className="teal-card" style={{ marginBottom: '1rem' }}>
        <div style={{ fontSize: '0.7rem', opacity: 0.7, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '0.25rem' }}>{t('opex.results.totalCostProject')}</div>
        <div style={{ fontFamily: 'var(--font-head)', fontSize: '2rem', fontWeight: 800 }}>{fmt(summary.totalCost)}</div>
        <div style={{ opacity: 0.8, fontSize: '0.8rem', marginTop: '0.25rem' }}>{fmt(summary.costPerPanel)}/{t('opex.results.sp.panel')}</div>
        <div style={{ marginTop: '1.25rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {items.map(([l, v]) => (
            <div key={l}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', marginBottom: '0.25rem' }}>
                <span style={{ opacity: 0.8 }}>{l}</span>
                <span style={{ fontWeight: 600 }}>{fmt(v)}</span>
              </div>
              <div style={{ height: 4, background: 'rgba(255,255,255,0.2)', borderRadius: 2 }}>
                <div style={{ height: '100%', background: 'rgba(255,255,255,0.8)', borderRadius: 2, width: `${Math.min(100, (v / summary.totalCost) * 100)}%` }} />
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="card">
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem', color: 'var(--primary)' }}>
          <TrendingUp size={18} />
          <span style={{ fontWeight: 700, fontFamily: 'var(--font-head)' }}>{t('opex.results.opOutputs')}</span>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '0.6rem' }}>
          {outputs.map(([label, val]) => (
            <div key={label} style={{ background: 'var(--surface-low)', borderRadius: 'var(--radius)', padding: '0.75rem' }}>
              <div style={{ fontSize: '0.65rem', color: 'var(--on-surface-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</div>
              <div style={{ fontFamily: 'var(--font-head)', fontWeight: 700, fontSize: '1rem', color: 'var(--primary)', marginTop: '0.2rem' }}>{val}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function OpexCalculator() {
  const { calculateOpex, saveScenario, trackScenario, listOpex, listCapex, calculateCapex, saveCapex } = useApp();
  const { t } = useTranslation();
  const location = useLocation();
  const isMobile = useIsMobile();

  const [tab, setTab] = useState('mm');
  const [resultMM, setResultMM] = useState(null);
  const [resultSP, setResultSP] = useState(null);
  const [resultSR, setResultSR] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [viewMode, setViewMode] = useState(false);
  const [capexMode, setCapexMode] = useState('manual'); // manual | linked | advisor
  const [capexList, setCapexList] = useState([]);
  const [selectedCapexId, setSelectedCapexId] = useState('');
  const [capexQuote, setCapexQuote] = useState(null);
  const [advisorConfig, setAdvisorConfig] = useState(null);
  const [advisorRecommendation, setAdvisorRecommendation] = useState(null);
  const [advisorEdited, setAdvisorEdited] = useState(false);
  const [advisorMaxTimeValue, setAdvisorMaxTimeValue] = useState(1);
  const [advisorMaxTimeUnit, setAdvisorMaxTimeUnit] = useState('days'); // hours | days

  const defaultMM = {
    workHours: 8, numPeople: 2, costPerPerson: 0,
    totalKg: 100, distance: 1000, altitude: 0, returnPayload: false,
    droneModel: 'FB3', numDrones: 1, maxWeightPerTrip: 100,
    numBatteries: 4, numChargers: 2, availableKw: 10,
    electricityCost: 0.25, batteryChangeTime: 3
  };

  const defaultSP = {
    workHours: 8, roofWorkers: 0, droneTeam: 2, costPerPerson: 0,
    totalPanels: 3400, panelWeight: 25, roofHeight: 10, groundDistance: 20,
    timeToPanel: 38, timeAttach: 25, timeToRoof: 56, timeRelease: 26,
    setupTime: 30, swapTime: 6, packupTime: 15,
    droneModel: 'FB3', numDrones: 1, numBatteries: 4,
    availableKw: 15, electricityCost: 0.25, flightTimePerBattery: 25
  };

  const defaultSR = {
    workHours: 8, numPeople: 2, costPerPerson: 0,
    payloadKg: 40, distance: 3400, altitude: 200,
    droneModel: 'FB3', numDrones: 1,
    availableKw: 10, electricityCost: 0.25
  };

  const [mm, setMm] = useState(defaultMM);
  const [sp, setSp] = useState(defaultSP);
  const [sr, setSr] = useState(defaultSR);

  const getScenarioType = () => (
    tab === 'sr' ? 'single_route' : (tab === 'sp' ? 'solar_panel' : 'middle_mile')
  );

  const getCurrentInputParams = () => ({
    ...(tab === 'mm' ? mm : tab === 'sp' ? sp : sr),
    calculatorType: getScenarioType(),
    capexMode,
    linkedCapexId: capexMode === 'linked' ? (selectedCapexId || null) : null
  });

  const trackCurrentScenario = async ({ results, dailyCost, name }) => {
    try {
      await trackScenario({
        name,
        scenarioType: getScenarioType(),
        inputParams: getCurrentInputParams(),
        results,
        dailyCost: dailyCost ?? null,
        roiMonths: null,
        configId: capexMode === 'linked' ? (selectedCapexId || null) : null,
        source: 'app'
      });
    } catch (trackError) {
      console.error('Scenario tracking failed', trackError);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const configs = await listCapex();
        setCapexList(configs || []);
      } catch (e) { console.error(e); }
    })();
  }, [listCapex]);

  useEffect(() => {
    const editId = location.state?.editId;
    if (!editId) return;
    loadScenarioById(editId);
  }, [location.state]);

  const loadScenarioById = async (id) => {
    try {
      const list = await listOpex();
      const scenario = (list || []).find(s => s.id === id);
      if (!scenario) return;
      const params = (() => {
        if (scenario.input_params && typeof scenario.input_params === 'object') return scenario.input_params;
        if (typeof scenario.input_params === 'string') {
          try { return JSON.parse(scenario.input_params); } catch (_) { return {}; }
        }
        return {};
      })();
      const inferredSingleRoute = (
        params?.calculatorType === 'single_route'
        || (Object.prototype.hasOwnProperty.call(params || {}, 'payloadKg') && !Object.prototype.hasOwnProperty.call(params || {}, 'totalKg'))
        || scenario.scenario_type === 'single_route'
        || String(scenario.name || '').toLowerCase().includes('scenario sr')
      );
      setCapexMode(params.capexMode || 'manual');
      setSelectedCapexId(params.linkedCapexId || '');
      if (inferredSingleRoute) {
        setTab('sr'); setSr({ ...defaultSR, ...params });
        if (scenario.results) setResultSR(scenario.results);
      } else if (scenario.scenario_type === 'middle_mile') {
        setTab('mm'); setMm({ ...defaultMM, ...params });
        if (scenario.results) setResultMM(scenario.results);
      } else {
        setTab('sp'); setSp({ ...defaultSP, ...params });
        if (scenario.results) setResultSP(scenario.results);
      }
      setViewMode(true);
    } catch(e) { console.error(e); }
  };

  const setMmField = (k) => (v) => { setViewMode(false); setMm(p => ({ ...p, [k]: v })); };
  const setSpField = (k) => (v) => { setViewMode(false); setSp(p => ({ ...p, [k]: v })); };
  const setSrField = (k) => (v) => { setViewMode(false); setSr(p => ({ ...p, [k]: v })); };

  const scenarioEquipment = tab === 'mm'
    ? { droneModel: mm.droneModel, numDrones: mm.numDrones, numBatteries: mm.numBatteries, numChargers: mm.numChargers, maxWeightPerTrip: mm.maxWeightPerTrip, distance: mm.distance }
    : tab === 'sp'
      ? { droneModel: sp.droneModel, numDrones: sp.numDrones, numBatteries: sp.numBatteries, maxWeightPerTrip: sp.panelWeight, distance: sp.groundDistance }
      : { droneModel: sr.droneModel, numDrones: sr.numDrones, numBatteries: 1, numChargers: 1, maxWeightPerTrip: sr.payloadKg, distance: sr.distance };

  const applyCapexToScenario = (cfg) => {
    if (!cfg) return;
    if (tab === 'mm') {
      setMm((p) => ({
        ...p,
        droneModel: cfg.drone_model || p.droneModel,
        numDrones: cfg.num_drones || p.numDrones,
        numBatteries: (cfg.num_battery || 0) + 1,
        numChargers: cfg.num_chargers || p.numChargers,
        maxWeightPerTrip: (cfg.drone_model || p.droneModel) === 'FB4' ? Math.min(200, p.maxWeightPerTrip || 200) : Math.min(100, p.maxWeightPerTrip || 100)
      }));
    } else if (tab === 'sp') {
      setSp((p) => ({
        ...p,
        droneModel: cfg.drone_model || p.droneModel,
        numDrones: cfg.num_drones || p.numDrones,
        numBatteries: (cfg.num_battery || 0) + 1
      }));
    } else {
      setSr((p) => ({
        ...p,
        droneModel: cfg.drone_model || p.droneModel,
        numDrones: cfg.num_drones || p.numDrones
      }));
    }
  };

  const buildCapexParams = (base = {}, options = {}) => {
    const { preferScenario = true } = options;
    const resolvedNumDrones = preferScenario ? scenarioEquipment.numDrones : (base.num_drones ?? scenarioEquipment.numDrones);
    const resolvedNumBatteries = preferScenario ? scenarioEquipment.numBatteries : ((base.num_battery ?? Math.max(0, (scenarioEquipment.numBatteries || 1) - 1)) + 1);
    const resolvedNumChargers = preferScenario
      ? (scenarioEquipment.numChargers ?? base.num_chargers ?? 0)
      : (base.num_chargers ?? scenarioEquipment.numChargers ?? 0);
    return {
      droneModel: preferScenario ? scenarioEquipment.droneModel : (base.drone_model || scenarioEquipment.droneModel),
      fb3Package: base.package_tier || 'fb3_base',
      numDrones: resolvedNumDrones,
      numBattery: Math.max(0, (resolvedNumBatteries || 1) - 1),
      numChargers: resolvedNumChargers,
      controllerType: base.controller_type || 'handheld',
      enhancedContainmentLevel: base.enhanced_containment || 'none',
      authorizationSupportLevel: base.authorization_support || 'vlos',
      mmsPackage: base.connectivity_package || 'mms_3m',
      supportWarrantyPackage: base.support_warranty || 'standard_12m',
      hasWinch: !!base.has_winch,
      hasLidar: !!base.has_lidar,
      hasStarlink: !!base.has_starlink,
      hasTrailer: !!base.has_trailer,
      hasBottomHook: !!base.has_bottom_hook,
      trainingA2: !!base.training_a2,
      trainingSts: !!base.training_sts,
      trainingCommCrm: !!base.training_comm_crm,
      trainingSora: !!base.training_sora,
      trainingPiloting: !!base.training_piloting,
      trainingMaintenance: !!base.training_maintenance
    };
  };

  useEffect(() => {
    if (capexMode !== 'linked') return;
    const selected = capexList.find((c) => c.id === selectedCapexId);
    if (!selected) return;
    applyCapexToScenario(selected);
  }, [selectedCapexId, capexMode, tab]);

  useEffect(() => {
    if (tab === 'sr' && capexMode !== 'manual') {
      setCapexMode('manual');
      setSelectedCapexId('');
      setAdvisorConfig(null);
      setAdvisorRecommendation(null);
      setAdvisorEdited(false);
      setCapexQuote(null);
    }
  }, [tab, capexMode]);

  useEffect(() => {
    let stop = false;
    (async () => {
      try {
        if (capexMode === 'linked') {
          const selected = capexList.find((c) => c.id === selectedCapexId);
          if (!selected) {
            if (!stop) setCapexQuote(null);
            return;
          }
          const quote = await calculateCapex(buildCapexParams(selected, { preferScenario: true }));
          if (!stop) setCapexQuote(quote);
        } else if (capexMode === 'advisor' && advisorConfig) {
          const quote = await calculateCapex(buildCapexParams(advisorConfig, { preferScenario: false }));
          if (!stop) setCapexQuote(quote);
        } else if (capexMode === 'advisor' && !advisorConfig) {
          if (!stop) setCapexQuote(null);
        } else if (capexMode === 'manual') {
          setCapexQuote(null);
        }
      } catch (e) {
        console.error(e);
      }
    })();
    return () => { stop = true; };
  }, [capexMode, selectedCapexId, advisorConfig, tab, mm.droneModel, mm.numDrones, mm.numBatteries, mm.numChargers, sp.droneModel, sp.numDrones, sp.numBatteries, sr.droneModel, sr.numDrones]);

  const getMaxMissionHours = () => {
    const maxTimeValue = Math.max(0, parseFloat(advisorMaxTimeValue) || 0);
    if (maxTimeValue <= 0) return 0;
    return advisorMaxTimeUnit === 'hours'
      ? maxTimeValue
      : maxTimeValue * (tab === 'mm' ? (mm.workHours || 8) : (sp.workHours || 8));
  };

  const getAdvisorBaseConfig = (droneModel) => {
    const requiresLongLink = (tab === 'mm' ? mm.distance : sp.groundDistance) > 5000;
    if (droneModel === 'FB4') {
      return {
        package_tier: 'fb3_max_ultra',
        controller_type: 'bvlos',
        enhanced_containment: '4g_rf',
        authorization_support: 'bvlos',
        connectivity_package: 'mms_24m',
        support_warranty: 'premium_24m'
      };
    }
    if (requiresLongLink) {
      return {
        package_tier: 'fb3_pro',
        controller_type: 'handheld_pro',
        enhanced_containment: 'rf_link',
        authorization_support: 'bvlos',
        connectivity_package: 'mms_12m',
        support_warranty: 'premium_24m'
      };
    }
    return {
      package_tier: 'fb3_base',
      controller_type: 'handheld',
      enhanced_containment: 'none',
      authorization_support: 'vlos',
      connectivity_package: 'mms_3m',
      support_warranty: 'standard_12m'
    };
  };

  const suggestCapexFromMission = async () => {
    const maxHours = getMaxMissionHours();
    if (maxHours <= 0) {
      setError(t('opex.errors.maxMissionTime'));
      return;
    }

    setError('');
    setAdvisorRecommendation(null);
    setAdvisorEdited(false);
    setLoading(true);
    try {
      const payload = tab === 'mm' ? mm.maxWeightPerTrip : sp.panelWeight;
      const models = payload > 100 ? ['FB4'] : ['FB3', 'FB4'];
      const evalCache = new Map();

      const evaluate = async (droneModel, numDrones, numBatteries) => {
        const cacheKey = `${tab}:${droneModel}:${numDrones}:${numBatteries}`;
        if (evalCache.has(cacheKey)) {
          return evalCache.get(cacheKey);
        }

        const chargersPerDrone = Math.max(1, Math.ceil((numBatteries - 1) / 2));
        const totalChargers = chargersPerDrone * numDrones;
        if (tab === 'mm') {
          if (mm.maxWeightPerTrip > (droneModel === 'FB4' ? 200 : 100)) {
            evalCache.set(cacheKey, null);
            return null;
          }
          const r = await calculateOpex('middle-mile', {
            ...mm,
            droneModel,
            numDrones,
            numBatteries,
            numChargers: chargersPerDrone
          });
          const fleetOperationalHours = parseFloat(r?.operations?.totalOperationalHours || 0);
          const missionHours = fleetOperationalHours > 0 ? (fleetOperationalHours / Math.max(1, numDrones)) : 0;
          const optimalBatterySets = parseInt(r?.operations?.optimalBatterySets || numBatteries, 10);
          const result = {
            ok: missionHours > 0 && missionHours <= maxHours,
            missionHours,
            result: r,
            chargersPerDrone,
            totalChargers,
            optimalBatterySets
          };
          evalCache.set(cacheKey, result);
          return result;
        }
        if (sp.panelWeight > (droneModel === 'FB4' ? 200 : 100)) {
          evalCache.set(cacheKey, null);
          return null;
        }
        const r = await calculateOpex('solar-panel', {
          ...sp,
          droneModel,
          numDrones,
          numBatteries
        });
        const missionHours = parseFloat(r?.operations?.totalWorkHours || 0);
        const optimalBatterySets = parseInt(r?.operations?.optimalBatterySets || numBatteries, 10);
        const result = {
          ok: missionHours > 0 && missionHours <= maxHours,
          missionHours,
          result: r,
          chargersPerDrone,
          totalChargers,
          optimalBatterySets
        };
        evalCache.set(cacheKey, result);
        return result;
      };

      const candidates = [];
      for (const droneModel of models) {
        let probeSingle = null;
        try {
          const firstProbe = await evaluate(droneModel, 1, 1);
          if (!firstProbe) continue;
          const singleSuggestedBatteries = Math.max(1, Math.min(30, firstProbe.optimalBatterySets || 1));
          probeSingle = singleSuggestedBatteries === 1
            ? firstProbe
            : await evaluate(droneModel, 1, singleSuggestedBatteries);
        } catch (_) {
          probeSingle = null;
        }
        if (!probeSingle) continue;

        const singleMissionHours = Math.max(0, parseFloat(probeSingle.missionHours || 0));
        if (singleMissionHours <= 0) continue;

        const estimatedDrones = Math.max(1, Math.ceil(singleMissionHours / maxHours));
        const droneCandidates = Array.from(
          new Set([estimatedDrones, estimatedDrones + 1, Math.max(1, estimatedDrones - 1)])
        ).filter((d) => d >= 1 && d <= 10);

        for (const d of droneCandidates) {
          let baseProbe = null;
          try {
            baseProbe = await evaluate(droneModel, d, Math.max(1, Math.min(30, probeSingle.optimalBatterySets || 1)));
          } catch (_) {
            baseProbe = null;
          }
          if (!baseProbe) continue;

          const advisedBatterySets = Math.max(
            1,
            Math.min(30, baseProbe.optimalBatterySets || probeSingle.optimalBatterySets || 1)
          );

          let finalProbe = baseProbe;
          if (advisedBatterySets !== Math.max(1, Math.min(30, probeSingle.optimalBatterySets || 1))) {
            try {
              const refined = await evaluate(droneModel, d, advisedBatterySets);
              if (refined) finalProbe = refined;
            } catch (_) {
              // keep base probe
            }
          }
          if (!finalProbe?.ok) continue;

          const testedBatteries = advisedBatterySets;
          const finalChargersPerDrone = Math.max(
            1,
            Math.ceil((testedBatteries - 1) / 2),
            Math.ceil((finalProbe?.optimalBatterySets || testedBatteries) / 3)
          );
          const totalBatterySets = testedBatteries * d;
          const totalExtraBatterySets = Math.max(0, totalBatterySets - d);
          const totalChargers = finalChargersPerDrone * d;
          const totalExtraChargers = Math.max(0, totalChargers - d);

          const baseConfig = getAdvisorBaseConfig(droneModel);
          const virtualCfg = {
            ...baseConfig,
            drone_model: droneModel,
            num_drones: d,
            num_battery: totalExtraBatterySets,
            num_chargers: totalExtraChargers
          };
          const quote = await calculateCapex(buildCapexParams(virtualCfg, { preferScenario: false }));
          candidates.push({
            droneModel,
            numDrones: d,
            numBatteries: testedBatteries,
            numChargers: finalChargersPerDrone,
            totalBatterySets,
            totalChargers,
            totalExtraChargers,
            missionHours: finalProbe.missionHours,
            opexResult: finalProbe.result,
            virtualCfg,
            quote
          });
        }
      }

      if (candidates.length === 0) {
        setError(t('opex.errors.noConfigForTime'));
        setCapexQuote(null);
        return;
      }

      candidates.sort((a, b) => (a.quote?.totalCapex || 0) - (b.quote?.totalCapex || 0));
      const best = candidates[0];
      setAdvisorConfig(best.virtualCfg);
      setCapexQuote(best.quote || null);
      setAdvisorRecommendation({
        droneModel: best.droneModel,
        numDrones: best.numDrones,
        batterySetsPerDrone: best.numBatteries,
        totalBatterySets: best.totalBatterySets,
        chargersPerDrone: best.numChargers,
        totalChargers: best.totalChargers,
        totalExtraChargers: best.totalExtraChargers,
        missionHours: best.missionHours
      });

      if (tab === 'mm') {
        setMm((p) => ({
          ...p,
          droneModel: best.droneModel,
          numDrones: best.numDrones,
          numBatteries: best.numBatteries,
          numChargers: best.totalChargers
        }));
        if (best.opexResult) {
          setResultMM({
            ...best.opexResult,
            operations: {
              ...best.opexResult.operations,
              totalOperationalHours: best.missionHours
            }
          });
        } else {
          setResultMM(null);
        }
      } else {
        setSp((p) => ({
          ...p,
          droneModel: best.droneModel,
          numDrones: best.numDrones,
          numBatteries: best.numBatteries
        }));
        setResultSP(best.opexResult || null);
      }
    } catch (e) {
      setError(e.response?.data?.error || t('opex.errors.advisorSuggest'));
    } finally {
      setLoading(false);
    }
  };

  const recalculateFromAdvisorConfig = async () => {
    if (!advisorRecommendation) return;
    const maxHours = getMaxMissionHours();
    if (maxHours <= 0) {
      setError(t('opex.errors.maxMissionTime'));
      return;
    }

    setError('');
    setLoading(true);
    try {
      const rec = advisorRecommendation;
      const droneModel = rec.droneModel;
      const numDrones = Math.max(1, parseInt(rec.numDrones, 10) || 1);
      const totalBatterySets = Math.max(numDrones, parseInt(rec.totalBatterySets, 10) || numDrones);
      const totalChargers = Math.max(numDrones, parseInt(rec.totalChargers, 10) || numDrones);
      const batterySetsPerDrone = Math.max(1, Math.ceil(totalBatterySets / numDrones));
      const chargersPerDrone = Math.max(1, Math.ceil(totalChargers / numDrones));

      if (tab === 'mm' && mm.maxWeightPerTrip > (droneModel === 'FB4' ? 200 : 100)) {
        setError(`Payload per trip non compatibile con ${droneModel}.`);
        return;
      }
      if (tab === 'sp' && sp.panelWeight > (droneModel === 'FB4' ? 200 : 100)) {
        setError(`Panel weight non compatibile con ${droneModel}.`);
        return;
      }

      const opexResult = tab === 'mm'
        ? await calculateOpex('middle-mile', {
            ...mm,
            droneModel,
            numDrones,
            numBatteries: batterySetsPerDrone,
            numChargers: chargersPerDrone
          })
        : await calculateOpex('solar-panel', {
            ...sp,
            droneModel,
            numDrones,
            numBatteries: batterySetsPerDrone
          });

      const fleetOperationalHours = tab === 'mm'
        ? parseFloat(opexResult?.operations?.totalOperationalHours || 0)
        : parseFloat(opexResult?.operations?.totalWorkHours || 0);
      const missionHours = tab === 'mm'
        ? (fleetOperationalHours > 0 ? fleetOperationalHours / numDrones : 0)
        : fleetOperationalHours;

      if (missionHours <= 0 || missionHours > maxHours) {
        setError(t('opex.errors.editedConfigTooSlow', { missionHours: missionHours.toFixed(2), maxHours: maxHours.toFixed(2) }));
      }

      const totalExtraBatterySets = Math.max(0, totalBatterySets - numDrones);
      const totalExtraChargers = Math.max(0, totalChargers - numDrones);
      const baseConfig = getAdvisorBaseConfig(droneModel);
      const virtualCfg = {
        ...baseConfig,
        drone_model: droneModel,
        num_drones: numDrones,
        num_battery: totalExtraBatterySets,
        num_chargers: totalExtraChargers
      };
      const quote = await calculateCapex(buildCapexParams(virtualCfg, { preferScenario: false }));

      setAdvisorConfig(virtualCfg);
      setCapexQuote(quote || null);
      setAdvisorRecommendation({
        droneModel,
        numDrones,
        batterySetsPerDrone,
        totalBatterySets,
        chargersPerDrone,
        totalChargers,
        totalExtraChargers,
        missionHours
      });
      setAdvisorEdited(false);

      if (tab === 'mm') {
        setMm((p) => ({
          ...p,
          droneModel,
          numDrones,
          numBatteries: batterySetsPerDrone,
          numChargers: totalChargers
        }));
        setResultMM({
          ...opexResult,
          operations: {
            ...opexResult.operations,
            totalOperationalHours: missionHours
          }
        });
      } else {
        setSp((p) => ({
          ...p,
          droneModel,
          numDrones,
          numBatteries: batterySetsPerDrone
        }));
        setResultSP(opexResult);
      }
    } catch (e) {
      setError(e.response?.data?.error || t('opex.errors.advisorRecalc'));
    } finally {
      setLoading(false);
    }
  };

  const calculate = async () => {
    setError(''); setLoading(true); setViewMode(false);
    try {
      const scenarioLabel = `Auto ${getScenarioType()} ${new Date().toLocaleString('it-IT')}`;

      if (tab === 'mm') {
        const r = await calculateOpex('middle-mile', mm);
        setResultMM(r);
        await trackCurrentScenario({ name: scenarioLabel, results: r, dailyCost: r?.summary?.totalCost });
      } else if (tab === 'sp') {
        const r = await calculateOpex('solar-panel', sp);
        setResultSP(r);
        await trackCurrentScenario({ name: scenarioLabel, results: r, dailyCost: r?.summary?.totalCost });
      } else {
        const r = await calculateOpex('single-route', sr);
        setResultSR(r);
        await trackCurrentScenario({ name: scenarioLabel, results: r, dailyCost: r?.summary?.totalCost });
      }
      setAdvisorRecommendation(null);
    } catch (e) {
      const calcError = e.response?.data?.error || t('opex.calcError');
      setError(calcError);
      await trackCurrentScenario({
        name: `Auto ${getScenarioType()} ${new Date().toLocaleString('it-IT')}`,
        results: {
          feasible: false,
          error: calcError,
          details: e.response?.data || null
        },
        dailyCost: null
      });
    }
    finally { setLoading(false); }
  };

  const save = async () => {
    const result = tab === 'mm' ? resultMM : tab === 'sp' ? resultSP : resultSR;
    if (!result) return;
    setSaving(true);
    try {
      let linkedCapexId = capexMode === 'linked' ? (selectedCapexId || null) : null;
      if (capexMode === 'advisor' && advisorConfig && capexQuote) {
        const advisorCapexParams = buildCapexParams(advisorConfig, { preferScenario: false });
        const savedCapex = await saveCapex({
          name: `Advisor ${advisorRecommendation?.droneModel || advisorCapexParams.droneModel} x${advisorRecommendation?.numDrones || advisorCapexParams.numDrones}`,
          ...advisorCapexParams,
          totalCapex: capexQuote.totalCapex,
          leasingMonthly: capexQuote.leasingMonthly
        });
        linkedCapexId = savedCapex?.id || null;
      }

      await saveScenario({
        name: `Scenario ${tab.toUpperCase()} ${new Date().toLocaleDateString('it-IT')}`,
        scenarioType: getScenarioType(),
        inputParams: {
          ...(tab === 'mm' ? mm : tab === 'sp' ? sp : sr),
          calculatorType: getScenarioType(),
          capexMode,
          linkedCapexId
        },
        results: result,
        dailyCost: result.summary.totalCost,
        roiMonths: 14,
        configId: linkedCapexId
      });
      alert(t('opex.savedAlert'));
    } catch (e) { alert(t('opex.saveError')); }
    finally { setSaving(false); }
  };

  const result = tab === 'mm' ? resultMM : tab === 'sp' ? resultSP : resultSR;

  return (
    <div className="page-content">
      <div style={{ marginBottom: '1.5rem', display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? '1rem' : 0, justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h1 style={{ fontFamily: 'var(--font-head)', fontSize: '1.75rem', fontWeight: 800, color: 'var(--primary)' }}>
            {t('opex.title')}
          </h1>
          <p style={{ color: 'var(--on-surface-muted)', marginTop: '0.25rem', fontSize: '0.9rem' }}>{t('opex.subtitle')}</p>
        </div>
        {viewMode && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 1rem', background: '#fef9c3', borderRadius: 8, fontSize: '0.8rem', fontWeight: 600, color: '#854d0e' }}>
            👁 {t('opex.viewMode')}
          </div>
        )}
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', borderBottom: '2px solid var(--surface-high)', marginBottom: '2rem' }}>
        <button className={`tab-btn${tab === 'sr' ? ' active' : ''}`} onClick={() => { setTab('sr'); setError(''); setViewMode(false); }}>
          {t('opex.tabs.sr')}
        </button>
        <button className={`tab-btn${tab === 'mm' ? ' active' : ''}`} onClick={() => { setTab('mm'); setError(''); setViewMode(false); }}>
          {t('opex.tabs.mm')}
        </button>
        <button className={`tab-btn${tab === 'sp' ? ' active' : ''}`} onClick={() => { setTab('sp'); setError(''); setViewMode(false); }}>
          {t('opex.tabs.sp')}
        </button>
      </div>

      <div className="card" style={{ marginBottom: '1.5rem', background:'linear-gradient(135deg, rgba(40,65,154,0.05), rgba(58,85,184,0.08))', border:'1px solid var(--surface-high)' }}>
        <div style={{ fontSize:'0.72rem', fontWeight:700, color:'var(--primary)', textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:'0.5rem' }}>
          {t(`opex.tabs.${tab}`)}
        </div>
        <h3 style={{ fontFamily:'var(--font-head)', fontWeight:800, fontSize:'1.2rem', color:'var(--primary)', marginBottom:'0.5rem' }}>
          {t(`opex.useCases.${tab}.title`)}
        </h3>
        <p style={{ color:'var(--on-surface)', lineHeight:1.6, marginBottom:'0.75rem' }}>
          {t(`opex.useCases.${tab}.desc`)}
        </p>
        <p style={{ color:'var(--on-surface-muted)', lineHeight:1.6, fontSize:'0.9rem' }}>
          <strong>{t('common.example')}:</strong> {t(`opex.useCases.${tab}.example`)}
        </p>
      </div>

      {error && (
        <div style={{ background: '#fee2e2', color: '#dc2626', padding: '0.875rem 1rem', borderRadius: 'var(--radius)', marginBottom: '1.5rem', fontSize: '0.875rem' }}>
          {error}
        </div>
      )}

      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: '0.5rem', marginBottom: '1rem' }}>
          <button className={capexMode === 'manual' ? 'btn-primary' : 'btn-secondary'} onClick={() => { setCapexMode('manual'); setSelectedCapexId(''); setAdvisorConfig(null); setAdvisorRecommendation(null); setAdvisorEdited(false); }}>
            {t('opex.modes.manual')}
          </button>
          {tab !== 'sr' && (
            <button className={capexMode === 'linked' ? 'btn-primary' : 'btn-secondary'} onClick={() => { setCapexMode('linked'); setAdvisorConfig(null); setAdvisorRecommendation(null); setAdvisorEdited(false); }}>
              {t('opex.modes.linkedQuote')}
            </button>
          )}
          {tab !== 'sr' && (
            <button className={capexMode === 'advisor' ? 'btn-primary' : 'btn-secondary'} onClick={() => { setCapexMode('advisor'); setSelectedCapexId(''); setAdvisorConfig(null); setCapexQuote(null); setAdvisorRecommendation(null); setAdvisorEdited(false); }}>
              {t('opex.modes.advisor')}
            </button>
          )}
        </div>

        {capexMode === 'linked' && (
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '1rem' }}>
            <div>
              <label className="label">{t('opex.advisor.savedQuoteConfig')}</label>
              <select className="input" value={selectedCapexId} onChange={(e) => setSelectedCapexId(e.target.value)}>
                <option value="">{t('opex.advisor.savedQuotePlaceholder')}</option>
                {capexList.map((c) => (
                  <option key={c.id} value={c.id}>{c.name} — {c.drone_model} x{c.num_drones}</option>
                ))}
              </select>
            </div>
            <div style={{ fontSize: '0.82rem', color: 'var(--on-surface-muted)', display:'flex', alignItems:'end' }}>
              {t('opex.advisor.savedQuoteHint')}
            </div>
          </div>
        )}

        {capexMode === 'advisor' && (
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '1rem', alignItems: 'end' }}>
            <div>
              <label className="label">{t('opex.advisor.maxMissionTime')}</label>
              <input className="input" type="number" min={0.1} step={0.1} value={advisorMaxTimeValue} onChange={(e) => setAdvisorMaxTimeValue(e.target.value)} />
            </div>
            <div>
              <label className="label">{t('opex.advisor.timeUnit')}</label>
              <select className="input" value={advisorMaxTimeUnit} onChange={(e) => setAdvisorMaxTimeUnit(e.target.value)}>
                <option value="hours">{t('opex.advisor.hours')}</option>
                <option value="days">{t('opex.advisor.days')}</option>
              </select>
            </div>
          </div>
        )}
        
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 360px', gap: '2rem', alignItems: 'start' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>

          {/* MIDDLE MILE */}
          {tab === 'mm' && <>
            <div className="card">
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.625rem', marginBottom: '1.25rem', color: 'var(--primary)' }}>
                <Users size={18} />
                <h3 style={{ fontFamily: 'var(--font-head)', fontWeight: 700 }}>{t('opex.mm.personnel')}</h3>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr', gap: '1rem' }}>
                <InputField label={t('opex.fields.workHours')}     value={mm.workHours}      onChange={setMmField('workHours')}     min={1} max={24} step={0.5} />
                <InputField label={t('opex.fields.numPeople')}     value={mm.numPeople}      onChange={setMmField('numPeople')}     min={2} note={t('opex.fields.numPeopleNote')} />
                <InputField label={t('opex.fields.costPerHr')}     value={mm.costPerPerson}  onChange={setMmField('costPerPerson')} min={0} />
              </div>
            </div>

            <div className="card">
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.625rem', marginBottom: '1.25rem', color: 'var(--primary)' }}>
                <Package size={18} />
                <h3 style={{ fontFamily: 'var(--font-head)', fontWeight: 700 }}>{t('opex.mm.cargoRoute')}</h3>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '1rem' }}>
                <InputField label={t('opex.fields.totalKg')}       value={mm.totalKg}        onChange={setMmField('totalKg')}       min={1} />
                <InputField label={t('opex.fields.distance')}      value={mm.distance}       onChange={setMmField('distance')}      min={1} step={10} />
                <InputField label={t('opex.fields.altitude')}      value={mm.altitude}       onChange={setMmField('altitude')}      min={0} step={10} note={t('opex.fields.altitudeNote')} />
                <ToggleField label={t('opex.fields.returnPayload')} value={mm.returnPayload}  onChange={setMmField('returnPayload')} note={t('opex.fields.returnPayloadNote')} />
              </div>
            </div>

            <div className="card">
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.625rem', marginBottom: '1.25rem', color: 'var(--primary)' }}>
                <Settings2 size={18} />
                <h3 style={{ fontFamily: 'var(--font-head)', fontWeight: 700 }}>{t('opex.mm.equipment')}</h3>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '1rem' }}>
                <InputField label={t('opex.fields.maxWeightTrip')}   value={mm.maxWeightPerTrip}  onChange={setMmField('maxWeightPerTrip')}  min={1} note={t('opex.fields.maxWeightNote')} />
                <InputField label={t('opex.fields.availableKw')}     value={mm.availableKw}       onChange={setMmField('availableKw')}       min={0.5} step={0.5} />
                <InputField label={t('opex.fields.electricityCost')} value={mm.electricityCost}   onChange={setMmField('electricityCost')}   min={0} step={0.01} />
                <InputField label={t('opex.fields.batteryChangeTime')} value={mm.batteryChangeTime} onChange={setMmField('batteryChangeTime')} min={1} step={0.5} />
                {capexMode !== 'advisor' ? (
                  <>
                    <InputField label={t('opex.fields.droneModel')} value={mm.droneModel}
                      onChange={v => { setMmField('droneModel')(v); setMmField('maxWeightPerTrip')(v === 'FB3' ? 100 : 200); }}
                      options={[{ val: 'FB3', label: 'FB3 – Max 100kg' }, { val: 'FB4', label: 'FB4 – Max 200kg' }]} />
                    <InputField label={t('opex.fields.numDrones')}       value={mm.numDrones}         onChange={setMmField('numDrones')}         min={1} max={10} />
                    <InputField label={t('opex.fields.batterySets')}     value={mm.numBatteries}      onChange={setMmField('numBatteries')}      min={1} max={50} />
                    <InputField label={t('opex.fields.chargers')}        value={mm.numChargers}       onChange={setMmField('numChargers')}       min={1} max={20} />
                  </>
                ) : (
                  <div style={{ gridColumn:'1 / -1', background:'var(--surface-low)', borderRadius:'var(--radius)', padding:'0.75rem', fontSize:'0.82rem', color:'var(--on-surface-muted)' }}>
                    {t('opex.advisor.autoConfigMM')}
                  </div>
                )}
              </div>
            </div>
          </>}

          {/* SOLAR PANEL */}
          {tab === 'sp' && <>
            <div className="card">
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.625rem', marginBottom: '1.25rem', color: 'var(--primary)' }}>
                <Users size={18} />
                <h3 style={{ fontFamily: 'var(--font-head)', fontWeight: 700 }}>{t('opex.sp.personnel')}</h3>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr 1fr', gap: '1rem' }}>
                <InputField label={t('opex.fields.workHours')}    value={sp.workHours}     onChange={setSpField('workHours')}    min={1} max={24} step={0.5} />
                
                <InputField label={t('opex.fields.droneTeam')}    value={sp.droneTeam}     onChange={setSpField('droneTeam')}    min={2} note={t('opex.fields.droneTeamNote')} />
                <InputField label={t('opex.fields.costPerHr')}    value={sp.costPerPerson} onChange={setSpField('costPerPerson')} min={0} />
              </div>
            </div>

            <div className="card">
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.625rem', marginBottom: '1.25rem', color: 'var(--primary)' }}>
                <Package size={18} />
                <h3 style={{ fontFamily: 'var(--font-head)', fontWeight: 700 }}>{t('opex.sp.panelSite')}</h3>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '1rem' }}>
                <InputField label={t('opex.fields.totalPanels')}    value={sp.totalPanels}    onChange={setSpField('totalPanels')}   min={1} />
                <InputField label={t('opex.fields.panelWeight')}    value={sp.panelWeight}    onChange={setSpField('panelWeight')}   min={1} step={0.5} />
                <InputField label={t('opex.fields.roofHeight')}     value={sp.roofHeight}     onChange={setSpField('roofHeight')}    min={1} note={t('opex.fields.roofHeightNote')} />
                <InputField label={t('opex.fields.groundDistance')} value={sp.groundDistance} onChange={setSpField('groundDistance')} min={1} note={t('opex.fields.groundDistNote')} />
              </div>
            </div>

            <div className="card">
              <h3 style={{ fontFamily: 'var(--font-head)', fontWeight: 700, marginBottom: '0.5rem', color: 'var(--primary)' }}>{t('opex.sp.cycleTitle')}</h3>
              <div style={{ background: 'var(--surface-low)', borderRadius: 'var(--radius)', padding: '0.75rem', marginBottom: '1rem', fontSize: '0.8rem', color: 'var(--on-surface-muted)' }}>
                ℹ️ {t('opex.sp.cycleNote')}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '1rem' }}>
                <InputField label={t('opex.fields.timeToPanel')}  value={sp.timeToPanel} onChange={setSpField('timeToPanel')} min={1} note={t('opex.fields.timeToPanelNote')} />
                <InputField label={t('opex.fields.timeAttach')}   value={sp.timeAttach}  onChange={setSpField('timeAttach')}  min={1} note={t('opex.fields.timeAttachNote')} />
                <InputField label={t('opex.fields.timeToRoof')}   value={sp.timeToRoof}  onChange={setSpField('timeToRoof')}  min={1} note={t('opex.fields.timeToRoofNote')} />
                <InputField label={t('opex.fields.timeRelease')}  value={sp.timeRelease} onChange={setSpField('timeRelease')} min={1} note={t('opex.fields.timeReleaseNote')} />
              </div>
            </div>

            <div className="card">
              <h3 style={{ fontFamily: 'var(--font-head)', fontWeight: 700, marginBottom: '0.5rem', color: 'var(--primary)' }}>{t('opex.sp.workdayTitle')}</h3>
              <div style={{ background: 'var(--surface-low)', borderRadius: 'var(--radius)', padding: '0.75rem', marginBottom: '1rem', fontSize: '0.8rem', color: 'var(--on-surface-muted)' }}>
                ℹ️ {t('opex.sp.workdayNote')}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr', gap: '1rem' }}>
                <InputField label={t('opex.fields.setupTime')}   value={sp.setupTime}   onChange={setSpField('setupTime')}   min={0} step={5}   note={t('opex.fields.setupTimeNote')} />
                <InputField label={t('opex.fields.swapTime')}    value={sp.swapTime}    onChange={setSpField('swapTime')}    min={0} step={0.5} note={t('opex.fields.swapTimeNote')} />
                <InputField label={t('opex.fields.packupTime')}  value={sp.packupTime}  onChange={setSpField('packupTime')}  min={0} step={5}   note={t('opex.fields.packupTimeNote')} />
              </div>
            </div>

            <div className="card">
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.625rem', marginBottom: '1.25rem', color: 'var(--primary)' }}>
                <Settings2 size={18} />
                <h3 style={{ fontFamily: 'var(--font-head)', fontWeight: 700 }}>{t('opex.sp.equipment')}</h3>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '1rem' }}>
                <InputField label={t('opex.fields.availableKw')}      value={sp.availableKw}      onChange={setSpField('availableKw')}      min={0.5} step={0.5} />
                <InputField label={t('opex.fields.electricityCost')}  value={sp.electricityCost}  onChange={setSpField('electricityCost')}  min={0} step={0.01} />
                <InputField label={t('opex.fields.flightTimeBat')}    value={sp.flightTimePerBattery} onChange={setSpField('flightTimePerBattery')} min={1} note={t('opex.fields.flightTimeBatNote')} />
                {capexMode !== 'advisor' ? (
                  <>
                    <InputField label={t('opex.fields.droneModel')}       value={sp.droneModel}       onChange={setSpField('droneModel')}       options={[{ val: 'FB3', label: 'FB3 – Max 100kg' }, { val: 'FB4', label: 'FB4 – Max 200kg' }]} />
                    <InputField label={t('opex.fields.numDrones')}        value={sp.numDrones}        onChange={setSpField('numDrones')}        min={1} max={10} />
                    <InputField label={t('opex.fields.batterySets')}      value={sp.numBatteries}     onChange={setSpField('numBatteries')}     min={1} max={50} />
                  </>
                ) : (
                  <div style={{ gridColumn:'1 / -1', background:'var(--surface-low)', borderRadius:'var(--radius)', padding:'0.75rem', fontSize:'0.82rem', color:'var(--on-surface-muted)' }}>
                    {t('opex.advisor.autoConfigSP')}
                  </div>
                )}
              </div>
            </div>
          </>}

          {/* SINGLE ROUTE */}
          {tab === 'sr' && <>
            <div className="card">
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.625rem', marginBottom: '1.25rem', color: 'var(--primary)' }}>
                <Users size={18} />
                <h3 style={{ fontFamily: 'var(--font-head)', fontWeight: 700 }}>Single Route — Personnel</h3>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr', gap: '1rem' }}>
                <InputField label={t('opex.fields.workHours')} value={sr.workHours} onChange={setSrField('workHours')} min={1} max={24} step={0.5} />
                <InputField label={t('opex.fields.numPeople')} value={sr.numPeople} onChange={setSrField('numPeople')} min={2} note={t('opex.fields.numPeopleNote')} />
                <InputField label={t('opex.fields.costPerHr')} value={sr.costPerPerson} onChange={setSrField('costPerPerson')} min={0} />
              </div>
            </div>

            <div className="card">
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.625rem', marginBottom: '1.25rem', color: 'var(--primary)' }}>
                <Package size={18} />
                <h3 style={{ fontFamily: 'var(--font-head)', fontWeight: 700 }}>Single Route — Mission</h3>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '1rem' }}>
                <InputField label="Payload (kg)" value={sr.payloadKg} onChange={setSrField('payloadKg')} min={1} />
                <InputField label={t('opex.fields.distance')} value={sr.distance} onChange={setSrField('distance')} min={1} step={10} />
                <InputField label={t('opex.fields.altitude')} value={sr.altitude} onChange={setSrField('altitude')} min={0} step={10} note={t('opex.fields.altitudeNote')} />
              </div>
            </div>

            <div className="card">
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.625rem', marginBottom: '1.25rem', color: 'var(--primary)' }}>
                <Settings2 size={18} />
                <h3 style={{ fontFamily: 'var(--font-head)', fontWeight: 700 }}>{t('opex.mm.equipment')}</h3>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '1rem' }}>
                <InputField label={t('opex.fields.availableKw')} value={sr.availableKw} onChange={setSrField('availableKw')} min={0.5} step={0.5} />
                <InputField label={t('opex.fields.electricityCost')} value={sr.electricityCost} onChange={setSrField('electricityCost')} min={0} step={0.01} />
                <InputField label={t('opex.fields.droneModel')} value={sr.droneModel}
                  onChange={setSrField('droneModel')}
                  options={[{ val: 'FB3', label: 'FB3 – Max 100kg' }, { val: 'FB4', label: 'FB4 – Max 200kg' }]} />
                <InputField label={t('opex.fields.numDrones')} value={sr.numDrones} onChange={setSrField('numDrones')} min={1} max={10} />
              </div>
            </div>
          </>}

          <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: '1rem' }}>
            {capexMode === 'advisor' && (
              <button className="btn-primary" onClick={suggestCapexFromMission} disabled={loading} style={{ flex: 1, justifyContent: 'center', padding: '1rem' }}>
                {loading ? t('opex.advisor.computing') : t('opex.advisor.computeAuto')}
              </button>
            )}
            {capexMode !== 'advisor' && (
              <button className="btn-primary" onClick={calculate} disabled={loading} style={{ flex: 1, justifyContent: 'center', padding: '1rem' }}>
                <Calculator size={18} /> {loading ? t('opex.calculating') : t('opex.calculate')}
              </button>
            )}
          </div>
        </div>

        {/* RISULTATI */}
        <div style={{ position: isMobile ? 'static' : 'sticky', top: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {capexQuote && (
            <div className="card">
              <div style={{ fontSize:'0.72rem', color:'var(--on-surface-muted)', textTransform:'uppercase', letterSpacing:'0.05em' }}>
                {t('opex.advisor.linkedQuote')}
              </div>
              <div style={{ fontFamily:'var(--font-head)', fontSize:'1.6rem', fontWeight:800, color:'var(--primary)', margin:'0.25rem 0 0.5rem' }}>
                {fmt(capexQuote.totalCapex)}
              </div>
              <div style={{ fontSize:'0.8rem', color:'var(--teal)', fontWeight:700, marginBottom:'0.5rem' }}>{capexQuote.packageLabel}</div>
              {advisorRecommendation && (
                <div style={{ marginBottom:'0.6rem', fontSize:'0.78rem', color:'var(--on-surface)' }}>
                  <div>{t('opex.advisor.fleet')}: {advisorRecommendation.numDrones} {t('common.drones').toLowerCase()}</div>
                  <div>{t('opex.advisor.totalBatteries')}: {advisorRecommendation.totalBatterySets} ({advisorRecommendation.batterySetsPerDrone}/drone)</div>
                  <div>{t('opex.advisor.totalChargers')}: {advisorRecommendation.totalChargers} ({advisorRecommendation.chargersPerDrone}/drone)</div>
                </div>
              )}
              <div style={{ display:'flex', flexDirection:'column', gap:'0.25rem' }}>
                {(capexQuote.includedItems || []).map((it) => (
                  <div key={it} style={{ fontSize:'0.76rem', color:'var(--on-surface)' }}>• {it}</div>
                ))}
              </div>
              {(capexQuote.selectedExtras || []).length > 0 && (
                <div style={{ marginTop:'0.6rem' }}>
                  <div style={{ fontSize:'0.7rem', color:'var(--on-surface-muted)', textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:'0.25rem' }}>
                    Extra selezionati
                  </div>
                  <div style={{ display:'flex', flexDirection:'column', gap:'0.2rem' }}>
                    {(capexQuote.selectedExtras || []).map((it) => (
                      <div key={it} style={{ fontSize:'0.76rem', color:'var(--on-surface)' }}>• {it}</div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          {capexMode === 'advisor' && advisorRecommendation && (
            <div className="card">
              <div style={{ fontSize:'0.72rem', color:'var(--on-surface-muted)', textTransform:'uppercase', letterSpacing:'0.05em' }}>
                {t('opex.advisor.suggestedConfig')}
              </div>
              <div style={{ marginTop:'0.35rem', fontSize:'0.78rem', color:'var(--on-surface-muted)' }}>
                {t('opex.advisor.suggestedHint')}
              </div>
              <div style={{ marginTop:'0.5rem', display:'grid', gridTemplateColumns:isMobile ? '1fr' : '1fr 1fr', gap:'0.5rem' }}>
                <div style={{ background:'var(--surface-low)', borderRadius:'var(--radius)', padding:'0.625rem' }}>
                  <div style={{ fontSize:'0.65rem', color:'var(--on-surface-muted)', textTransform:'uppercase' }}>Drone</div>
                  <select
                    className="input"
                    value={advisorRecommendation.droneModel}
                    onChange={(e) => {
                      const nextModel = e.target.value;
                      setAdvisorRecommendation((p) => ({ ...p, droneModel: nextModel }));
                      setAdvisorEdited(true);
                    }}
                  >
                    <option value="FB3">FB3</option>
                    <option value="FB4">FB4</option>
                  </select>
                </div>
                <div style={{ background:'var(--surface-low)', borderRadius:'var(--radius)', padding:'0.625rem' }}>
                  <div style={{ fontSize:'0.65rem', color:'var(--on-surface-muted)', textTransform:'uppercase' }}>{t('opex.fields.numDrones')}</div>
                  <input
                    className="input"
                    type="number"
                    min={1}
                    max={20}
                    value={advisorRecommendation.numDrones}
                    onChange={(e) => {
                      const numDrones = Math.max(1, parseInt(e.target.value || '1', 10));
                      setAdvisorRecommendation((p) => ({ ...p, numDrones }));
                      setAdvisorEdited(true);
                    }}
                  />
                </div>
                <div style={{ background:'var(--surface-low)', borderRadius:'var(--radius)', padding:'0.625rem' }}>
                  <div style={{ fontSize:'0.65rem', color:'var(--on-surface-muted)', textTransform:'uppercase' }}>Batterie totali</div>
                  <input
                    className="input"
                    type="number"
                    min={1}
                    max={200}
                    value={advisorRecommendation.totalBatterySets}
                    onChange={(e) => {
                      const totalBatterySets = Math.max(1, parseInt(e.target.value || '1', 10));
                      setAdvisorRecommendation((p) => ({ ...p, totalBatterySets }));
                      setAdvisorEdited(true);
                    }}
                  />
                </div>
                <div style={{ background:'var(--surface-low)', borderRadius:'var(--radius)', padding:'0.625rem' }}>
                  <div style={{ fontSize:'0.65rem', color:'var(--on-surface-muted)', textTransform:'uppercase' }}>Charger totali</div>
                  <input
                    className="input"
                    type="number"
                    min={1}
                    max={200}
                    value={advisorRecommendation.totalChargers}
                    onChange={(e) => {
                      const totalChargers = Math.max(1, parseInt(e.target.value || '1', 10));
                      setAdvisorRecommendation((p) => ({ ...p, totalChargers }));
                      setAdvisorEdited(true);
                    }}
                  />
                </div>
              </div>
              <div style={{ marginTop:'0.5rem', fontSize:'0.78rem', color:'var(--on-surface-muted)' }}>
                {t('opex.advisor.missionTimeEstimated', { missionHours: advisorRecommendation.missionHours.toFixed(2), dailyTarget: (tab === 'mm' ? mm.workHours : sp.workHours) })}
              </div>
              {advisorEdited && (
                <div style={{ marginTop:'0.5rem', fontSize:'0.78rem', color:'#9a3412', background:'#ffedd5', borderRadius:8, padding:'0.5rem 0.625rem' }}>
                  {t('opex.advisor.unsavedChanges')}
                </div>
              )}
              {advisorEdited && (
                <button
                  className="btn-primary"
                  onClick={recalculateFromAdvisorConfig}
                  disabled={loading}
                  style={{ width:'100%', marginTop:'0.75rem', justifyContent:'center' }}
                >
                  {loading ? t('opex.advisor.computing') : t('opex.advisor.applyAndRecalc')}
                </button>
              )}
            </div>
          )}
          {tab === 'mm' ? <ResultsMM result={resultMM} isMobile={isMobile} /> : tab === 'sp' ? <ResultsSP result={resultSP} isMobile={isMobile} /> : <ResultsMM result={resultSR} isMobile={isMobile} />}
          {result && (
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button className="btn-secondary" style={{ flex: 1, justifyContent: 'center' }}>
                <FileDown size={16} /> {t('opex.exportPdf')}
              </button>
              {!viewMode && (
                <button className="btn-primary" onClick={save} disabled={saving} style={{ flex: 1, justifyContent: 'center' }}>
                  <Save size={16} /> {saving ? t('opex.saving') : t('opex.save')}
                </button>
              )}
              {viewMode && (
                <button className="btn-primary" onClick={() => setViewMode(false)} style={{ flex: 1, justifyContent: 'center' }}>
                  ✏️ {t('opex.edit')}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
